import { type ReactNode, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getDomainAssessment, getRecommendations } from '../../lib/api';
import { SkeletonText } from '../ui/skeleton';
import { InfoButton } from '../ui/info-button';
import { UtilizationScore, UtilizationScoreModal } from '../UtilizationScore';
import { UnbilledCloudsNote } from '../UnbilledCloudsNote';
import { scoreBand, usd } from '../../lib/graph-visuals';
import { monthStart, realizedBetween, realizedSince, recoverableSaving } from '../../lib/savings';

const pct = (n: number) => `${Math.round(n * 100)}%`;

/** Shared tile chrome so the header metrics line up cleanly. */
function Tile({ label, info, children }: { label: string; info?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
      {/* Grow the value block so the label always anchors to the tile bottom —
          keeps every tile's label on the same line despite differing value heights. */}
      <div className="min-h-[2.5rem] flex-1">{children}</div>
      <div className="flex items-center gap-1.5">
        <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
          {label}
        </span>
        {info}
      </div>
    </div>
  );
}

/** A ▲/▼ month-over-month chip for realized savings. */
function MoMChip({ current, previous }: { current: number; previous: number }) {
  if (previous <= 0) {
    return (
      <span className="text-[0.65rem] text-muted">{current > 0 ? 'new this month' : '—'}</span>
    );
  }
  const pctChange = Math.round(((current - previous) / previous) * 100);
  const up = pctChange >= 0;
  return (
    <span className={`text-[0.65rem] font-semibold ${up ? 'text-success' : 'text-danger'}`}>
      {up ? '▲' : '▼'} {up ? '+' : ''}
      {pctChange}% vs last month
    </span>
  );
}

/**
 * The domain's headline metrics (DASH-004/007): the reused Utilization Score
 * (with an "ⓘ" for the method), estimated monthly spend, and how much was realized
 * this month with a month-over-month trend. The recommendation pipeline lives in
 * its own Cost Optimization Status section below.
 */
export function DomainHealthHeader({ domainId }: { domainId: string }) {
  const assessment = useQuery({
    queryKey: ['domain-assessment', domainId],
    queryFn: () => getDomainAssessment(domainId),
    refetchInterval: 60_000,
    // Keep the spend figure stable across remounts/refetches so it never briefly
    // flashes the list-price estimate before the billed run rate settles.
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
    refetchInterval: 60_000,
  });
  const [scoreOpen, setScoreOpen] = useState(false);
  const a = assessment.data;

  if (!a) {
    return (
      <div className="rounded-md border border-hairline bg-surface-2 p-5">
        <SkeletonText lines={2} lineClassName="h-4" />
      </div>
    );
  }

  const band = a.utilizationScore != null ? scoreBand(a.utilizationScore) : null;
  const now = new Date();
  const thisMonth = monthStart(now);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const realizedThisMonth = recs.data ? realizedSince(recs.data.items, thisMonth) : 0;
  const realizedLastMonth = recs.data ? realizedBetween(recs.data.items, lastMonth, thisMonth) : 0;

  // Every non-terminal opportunity (open/accepted/in validation), consistent with
  // the org box and the by-category breakdown.
  const savingsPotential = recs.data ? recoverableSaving(recs.data.items) : 0;

  // Cloud spend, consistent with the org box: billed cost to date + projected run
  // rate where we have a billed signal, otherwise our estimate (flagged).
  const hasBilled = a.billedMonthlyCost != null && a.billedMonthlyCost > 0;
  const spendToDate = hasBilled ? a.billedMonthlyCost! : a.totalMonthlyCost;
  const spendRunRate = hasBilled
    ? (a.projectedBilledCost ?? a.billedMonthlyCost!)
    : a.totalMonthlyCost;

  return (
    <div className="space-y-3">
      <div className="flex flex-col divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-surface-2 md:flex-row md:divide-x md:divide-y-0">
        <Tile
          label="Utilization score"
          info={
            <button
              type="button"
              aria-label="How the utilization score is calculated"
              onClick={() => setScoreOpen(true)}
              className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-full border border-hairline text-[0.6rem] font-bold text-muted transition-colors hover:border-iris hover:text-iris"
            >
              i
            </button>
          }
        >
          <div className="flex flex-col gap-1">
            <UtilizationScore score={a.utilizationScore} size="lg" />
            {band && <span className={`text-xs font-semibold ${band.tone}`}>{band.label}</span>}
          </div>
        </Tile>

        <Tile
          label="Monthly cloud spend"
          info={
            <InfoButton label="Monthly cloud spend">
              <p>
                This domain&apos;s cloud spend this month: the{' '}
                <b className="text-bone">recorded cost to date</b> (your provider&apos;s billed
                charges where we have them, otherwise our estimate) and the projected{' '}
                <b className="text-bone">run rate</b> for the full month.
              </p>
              <p>
                {pct(a.coverage)} of the spend has usage metrics — real CPU/memory/traffic data — so
                the Utilization Score reflects that portion.
              </p>
            </InfoButton>
          }
        >
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl font-bold leading-none tracking-tight text-bone">
              {spendToDate > 0 ? usd(spendToDate) : '—'}
            </span>
            {!hasBilled && spendToDate > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-hairline px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-muted"
                title="No billed signal yet — this is our reconciled estimate."
              >
                <span className="h-1.5 w-1.5 rounded-full bg-muted" aria-hidden />
                Est.
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-haze">
            Recorded to date · ~{usd(spendRunRate)} run rate
          </div>
        </Tile>

        <Tile label="Monthly savings potential">
          <div className="font-display text-3xl font-bold leading-none tracking-tight text-iris">
            {savingsPotential > 0 ? usd(savingsPotential) : '—'}
          </div>
          <div className="mt-1 text-xs text-haze">recoverable / month (estimated)</div>
        </Tile>

        <Tile label="Realized savings this month">
          <div className="font-display text-3xl font-bold leading-none tracking-tight text-iris">
            {usd(realizedThisMonth)}
            <span className="text-sm font-normal text-muted">/mo</span>
          </div>
          <div className="mt-1">
            <MoMChip current={realizedThisMonth} previous={realizedLastMonth} />
          </div>
        </Tile>

        {scoreOpen && <UtilizationScoreModal assessment={a} onClose={() => setScoreOpen(false)} />}
      </div>
      {hasBilled && <UnbilledCloudsNote clouds={a.unbilledClouds} />}
    </div>
  );
}
